import Image from "next/image";
import classNames from "classnames";
import NextLink from "next/link";
import { Canvas, Typography, Link } from "@cappelendamm/design-system/react";
import { DigitalServiceItem } from "../types";
import { ListingTags } from "@/search/components/item-listings/ListingTags";
import { ListingItemProps } from "./types";
import { Book } from "iconoir-react";
import { selectCollection } from "./selectors";
import { PricelistPrice } from "@/products/components/PricelistPrice";
import {
  PRODUCT_ARTICLE_CANVAS_HEIGHT,
  PRODUCT_ARTICLE_IMAGE_HEIGHT,
  PRODUCT_ARTICLE_IMAGE_WIDTH,
  PRODUCT_ARTICLE_TITLE_CLASS_NAME,
} from "./BookListingItem";

export function DigitalServiceListingItem({
  item,
  config,
}: ListingItemProps<DigitalServiceItem>) {
  const {
    objectID: sku,
    imageUrl,
    price,
    subjectToVAT,
    name,
    priceLists,
    itemDetails: { binding: format, language, contributorByLine, slug },
  } = item;

  const href = slug ? `/digitale-tjenester/${slug}` : null;
  const collection = config?.fields?.series ? selectCollection(item) : null;
  const meta = { format, language };

  return (
    <article
      className={classNames(
        "row-span-[var(--list-row-count)] grid min-w-0 grid-rows-subgrid gap-1",
        { "cursor-pointer": href },
        href
          ? "active:rounded active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]"
          : "",
      )}
    >
      <NextLink
        href={href ?? ""}
        aria-labelledby={`item-${sku}`}
        className="group relative row-start-1 -row-end-3 flex min-w-0 flex-col gap-1 no-underline"
      >
        <Canvas
          className="mb-1 px-2 py-2 md:px-6 md:py-2"
          style={{ height: PRODUCT_ARTICLE_CANVAS_HEIGHT }}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={`Omslag av "${name}"`}
              width={PRODUCT_ARTICLE_IMAGE_WIDTH}
              height={PRODUCT_ARTICLE_IMAGE_HEIGHT}
              className="h-full w-auto object-contain transition-all sm:group-hover:scale-102 sm:group-hover:shadow-lg"
              quality={85}
            />
          ) : (
            <Book
              width={PRODUCT_ARTICLE_IMAGE_WIDTH}
              height={PRODUCT_ARTICLE_IMAGE_WIDTH}
              className="text-gray-200 transition-transform sm:group-hover:scale-102 sm:group-hover:text-gray-400"
            />
          )}
        </Canvas>
        <div className="flex flex-col gap-1">
          {config?.tags && Object.values(config.tags).some(Boolean) && (
            <div className="overflow-hidden empty:hidden">
              <ListingTags
                item={item}
                config={{
                  showHighlighted: config?.tags?.highlighted,
                  showInformational: config?.tags?.informational,
                  showPromoted: config?.tags?.promoted,
                  showPublicationDate: config?.tags?.publicationDate,
                }}
              />
            </div>
          )}
          <Typography
            id={`item-${sku}`}
            as="h3"
            variant="title-md"
            className={classNames(PRODUCT_ARTICLE_TITLE_CLASS_NAME, {
              "sm:group-hover:underline": href,
            })}
          >
            {name}
          </Typography>
        </div>
        <Typography
          as="p"
          variant="body-base"
          className="mt-0 block truncate"
          muted
        >
          {contributorByLine}
        </Typography>
      </NextLink>
      {config?.fields?.formatMeta || collection ? (
        <div className="-row-end-2 mt-4 flex min-w-0 flex-col gap-1 text-sm">
          {config?.fields?.formatMeta ? (
            <div className="flex gap-2 [&_span]:first-of-type:font-semibold [&_span:not(:first-of-type)]:border-l [&_span:not(:first-of-type)]:pl-2">
              {Object.entries(meta)
                .filter(([, value]) => Boolean(value))
                .map(([key, value]) => (
                  <span key={key}>{value}</span>
                ))}
            </div>
          ) : null}
          {collection ? (
            <div className="truncate">
              {collection.prefix}
              {collection.href ? (
                <Link
                  as={NextLink}
                  className="inline p-0 font-semibold"
                  href={collection.href}
                >
                  {collection.linkName}
                </Link>
              ) : (
                collection.linkName
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="-row-start-1 flex min-h-8 items-center self-start text-3xl font-bold [&_span]:text-left">
        <PricelistPrice
          originalItemPrice={price}
          subjectToVAT={subjectToVAT}
          pricelists={priceLists ?? []}
        />
      </div>
    </article>
  );
}
